import React, { useState, useRef } from 'react';
import type { ApprovalStage } from '@/types/approvalWorkflow';
import { FlowNode } from './FlowNode';
import { FlowConnection } from './FlowConnection';
import { FlowControls } from './FlowControls';
import { getStageRuleCount, stageHasRules } from './stageRules';
import { Card } from '@/components/ui/card';
import { Workflow } from 'lucide-react';
import html2canvas from 'html2canvas';

interface WorkflowVisualFlowProps {
  stages: ApprovalStage[];
  onEditStage?: (stageId: string) => void;
}

export const WorkflowVisualFlow: React.FC<WorkflowVisualFlowProps> = ({
  stages,
  onEditStage,
}) => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const flowRef = useRef<HTMLDivElement>(null);

  // Empty state
  if (stages.length === 0) {
    return (
      <div className="flex items-center justify-center h-[600px]">
        <Card className="p-8 text-center max-w-md">
          <Workflow className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">No Stages Yet</h3>
          <p className="text-sm text-muted-foreground">
            Add stages to your workflow to see the visual flow diagram.
          </p>
        </Card>
      </div>
    );
  }

  // Zoom controls
  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.1, 2));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.1, 0.5));
  };

  const handleFitView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleReset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Export functionality
  const handleExport = async () => {
    if (!flowRef.current) return;

    try {
      const canvas = await html2canvas(flowRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
      });

      const link = document.createElement('a');
      link.download = 'workflow-diagram.png';
      link.href = canvas.toDataURL();
      link.click();
    } catch (error) {
      console.error('Failed to export workflow:', error);
    }
  };

  // Pan controls
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0 && !(e.target as HTMLElement).closest?.('.flow-node')) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Wheel zoom
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom((prev) => Math.max(0.5, Math.min(2, prev + delta)));
  };

  // Detect if stages have rules (for conditional flow)
  const hasConditionalFlow = stages.some((stage) => stageHasRules(stage));

  return (
    <div className="relative h-[calc(100vh-300px)] min-h-[600px] bg-muted/30 rounded-lg overflow-hidden">
      {/* Flow Controls */}
      <FlowControls
        zoom={zoom}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onFitView={handleFitView}
        onReset={handleReset}
        onExport={handleExport}
      />

      {/* Flow Container */}
      <div
        ref={containerRef}
        className="w-full h-full overflow-hidden cursor-grab active:cursor-grabbing"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        <div
          ref={flowRef}
          className="flex flex-col items-center py-12 transition-transform duration-200"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: 'center top',
          }}
        >
          {/* Legend */}
          <div className="mb-8 flex items-center gap-6 text-xs text-muted-foreground bg-background/80 backdrop-blur-sm px-4 py-2 rounded-lg border">
            <div className="flex items-center gap-2">
              <div className="w-8 h-0.5 bg-muted-foreground" />
              <span>Sequential</span>
            </div>
            {hasConditionalFlow && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-0.5 border-t-2 border-dashed border-amber-500" />
                <span>Conditional</span>
              </div>
            )}
          </div>

          {/* Flow Nodes */}
          <div className="space-y-0">
            {stages.map((stage, index) => {
              const isStart = index === 0;
              const isEnd = index === stages.length - 1;
              const hasRules = stageHasRules(stage);
              const ruleCount = getStageRuleCount(stage);

              return (
                <div key={stage.id} className="flex flex-col items-center">
                  {/* Node */}
                  <div className="flow-node">
                    <FlowNode
                      stage={stage}
                      isStart={isStart}
                      isEnd={isEnd}
                      onEdit={onEditStage ? () => onEditStage(stage.id) : undefined}
                    />
                  </div>

                  {/* Connection to next stage */}
                  {!isEnd && (
                    <FlowConnection
                      type={hasRules ? 'conditional' : 'default'}
                      label={hasRules ? `${ruleCount} rules` : undefined}
                      isVertical={true}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Workflow Summary */}
          <div className="mt-8 bg-background/80 backdrop-blur-sm px-6 py-3 rounded-lg border">
            <div className="flex items-center gap-6 text-sm">
              <div>
                <span className="text-muted-foreground">Total Stages:</span>{' '}
                <span className="font-semibold">{stages.length}</span>
              </div>
              <div className="h-4 w-px bg-border" />
              <div>
                <span className="text-muted-foreground">Total Approvers:</span>{' '}
                <span className="font-semibold">
                  {stages.reduce((sum, stage) => sum + stage.approvers.length, 0)}
                </span>
              </div>
              <div className="h-4 w-px bg-border" />
              <div>
                <span className="text-muted-foreground">Conditional Stages:</span>{' '}
                <span className="font-semibold">
                  {stages.filter((s) => stageHasRules(s)).length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
